import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { createDept, getDept, listDepts, updateDept, type DeptRow } from '../../api/orgApi.js';
import { Field, FormShell, SaveBar, SelectField, todayIso } from './orgKit.js';

export function DepartmentFormPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { deptId } = useParams<{ deptId: string }>();
  const editing = Boolean(deptId);

  const [deptCode, setDeptCode] = useState('');
  const [deptName, setDeptName] = useState('');
  const [parentDeptId, setParentDeptId] = useState('');
  const [effectiveFromDate, setEffectiveFromDate] = useState(todayIso());
  const [depts, setDepts] = useState<DeptRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listDepts(token).then((r) => setDepts(r.data)).catch((e) => setError((e as Error).message));
  }, [token]);

  useEffect(() => {
    if (!deptId) return;
    getDept(token, deptId)
      .then((d) => {
        setDeptCode(d.deptCode);
        setDeptName(d.deptName);
        setParentDeptId(d.parentDeptId ?? '');
        setEffectiveFromDate(d.effectiveFromDate ?? todayIso());
      })
      .catch((e) => setError((e as Error).message));
  }, [token, deptId]);

  async function submit(): Promise<void> {
    if (!deptName.trim()) { setError(t('org.department.form.nameReq')); return; }
    if (!editing && !deptCode.trim()) { setError(t('org.department.form.codeReq')); return; }
    setError(null);
    setBusy(true);
    try {
      if (editing && deptId) {
        await updateDept(token, deptId, {
          deptName: deptName.trim(),
          parentDeptId: parentDeptId || undefined,
        });
      } else {
        await createDept(token, {
          deptCode: deptCode.trim(),
          deptName: deptName.trim(),
          parentDeptId: parentDeptId || undefined,
          effectiveFromDate,
        });
      }
      navigate('/org/departments');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const parentOptions = depts
    .filter((d) => d.deptId !== deptId)
    .map((d) => ({ value: d.deptId, label: `${d.deptCode} — ${d.deptName}` }));

  return (
    <FormShell label="department-form" title={editing ? t('org.department.form.editTitle') : t('org.department.form.newTitle')} error={error} onSubmit={submit}>
      <Field label={t('org.field.code')} name="deptCode" value={deptCode} onChange={setDeptCode} required placeholder={t('org.department.form.codePh')} />
      <Field label={t('org.field.name')} name="deptName" value={deptName} onChange={setDeptName} required />
      <SelectField label={t('org.field.parentDept')} name="parentDeptId" value={parentDeptId} onChange={setParentDeptId} options={parentOptions} placeholder={t('org.common.noneTop')} />
      {!editing && (
        <Field label={t('org.field.effectiveFrom')} name="effectiveFromDate" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
      )}
      <SaveBar busy={busy} saveLabel={editing ? t('org.common.saveChanges') : t('org.department.form.create')} cancelTo="/org/departments" />
    </FormShell>
  );
}
